import React from 'react';
import { Form, Row, Col, Input, Select, Tag } from 'antd';

type MetaFields = {
  form_description: string;
  form_ref: string;
  reference_document: string;
  numbering_prefix: string;
};

interface FormMetaSectionProps {
  metaFields: MetaFields;
  onMetaChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  formGroups: { id: number; group_name: string; essential_field_names: string[] }[];
  selectedGroupId: number | null;
  onGroupChange: (value: number | null) => void;
  groupEssentialFields: string[];
  groupLoading: boolean;
  form: any;
}

const FormMetaSection: React.FC<FormMetaSectionProps> = ({
  metaFields,
  onMetaChange,
  formGroups,
  selectedGroupId,
  onGroupChange,
  groupEssentialFields,
  groupLoading,
  form,
}) => {
  return (
    <Form 
      layout="vertical" 
      form={form} 
      style={{ 
        maxWidth: 900, 
        display: 'flex', 
        flexDirection: 'row', 
        gap: 24, 
        alignItems: 'flex-start', 
        marginBottom: 0 
      }}
    >
      {/* Left: Meta fields */}
      <div style={{ flex: 2 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Form Description" required>
              <Input 
                name="form_description" 
                value={metaFields.form_description} 
                onChange={onMetaChange}
                onBlur={(e) => {
                  const up = (e.target.value || '').toUpperCase();
                  if (up !== e.target.value) {
                    onMetaChange({ target: { name: 'form_description', value: up } } as any);
                  }
                }} 
                placeholder="Enter form description" 
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Form Ref" required>
              <Input 
                name="form_ref" 
                value={metaFields.form_ref} 
                onChange={onMetaChange}
                onBlur={(e) => {
                  const up = (e.target.value || '').toUpperCase();
                  if (up !== e.target.value) {
                    onMetaChange({ target: { name: 'form_ref', value: up } } as any);
                  }
                }} 
                placeholder="Enter form reference" 
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Reference Document">
              <Input 
                name="reference_document" 
                value={metaFields.reference_document} 
                onChange={onMetaChange}
                onBlur={(e) => {
                  const up = (e.target.value || '').toUpperCase();
                  if (up !== e.target.value) {
                    onMetaChange({ target: { name: 'reference_document', value: up } } as any);
                  }
                }} 
                placeholder="Reference document info" 
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Numbering Prefix for Filled Forms" required>
              <Input 
                name="numbering_prefix" 
                value={metaFields.numbering_prefix} 
                onChange={onMetaChange}
                onBlur={(e) => {
                  const up = (e.target.value || '').toUpperCase();
                  if (up !== e.target.value) {
                    onMetaChange({ target: { name: 'numbering_prefix', value: up } } as any);
                  }
                }} 
                placeholder="e.g. WP-" 
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* Right: Essential fields compartment */}
      <div
        style={{
          flex: 1,
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          padding: 10,
          background: '#fafbfc',
          minWidth: 220,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.03)',
        }}
      >
        <span style={{ fontWeight: 600, marginBottom: 4 }}>Form Group</span>
        <Select
          style={{ width: '100%', marginBottom: 8 }}
          placeholder={groupLoading ? 'Loading groups...' : 'Select a form group'}
          loading={groupLoading}
          value={selectedGroupId}
          onChange={onGroupChange}
          options={formGroups.map(g => ({ value: g.id, label: g.group_name }))}
          allowClear
          disabled={groupLoading || formGroups.length === 0}
        />
        <span style={{ fontWeight: 500, marginBottom: 4 }}>Essential Fields:</span>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            marginTop: 2,
            maxWidth: '100%',
            minHeight: 28,
          }}
        >
          {selectedGroupId && groupEssentialFields.length > 0
            ? groupEssentialFields.map(f => <Tag key={f}>{f}</Tag>)
            : <span style={{ color: '#888' }}>(none)</span>}
        </div>
      </div>
    </Form>
  );
};

export default FormMetaSection;